export function parseColIndex(col: string): number {
  let idx = 0;
  for (let i = 0; i < col.length; i++) {
    idx = idx * 26 + (col.charCodeAt(i) - 64);
  }
  return idx;
}

export function colLabel(idx: number): string {
  let label = '';
  while (idx > 0) {
    idx--;
    label = String.fromCharCode(65 + (idx % 26)) + label;
    idx = Math.floor(idx / 26);
  }
  return label;
}

export function parseCellRef(ref: string): { col: number; row: number } | null {
  const match = ref.match(/^([A-Z]+)(\d+)$/);
  if (!match) return null;
  return { col: parseColIndex(match[1]), row: parseInt(match[2], 10) };
}

export const ROW_TYPE_MAP: Record<string, string> = {
  MM_HEADER: 'ss-row-type-header',
  MM_UNIT: 'ss-row-type-unit',
  MM_SAMPLE: 'ss-row-type-sample',
  MM_LABEL: 'ss-row-type-label',
  MM_COMPUTED: 'ss-row-type-computed',
};

export function resolveSheets(data: Record<string, any>): Record<string, Record<string, any>> {
  const firstKey = Object.keys(data)[0];
  if (!firstKey) return data;
  const firstValue = data[firstKey];
  if (firstValue && typeof firstValue === 'object' && !Array.isArray(firstValue)) {
    const innerKeys = Object.keys(firstValue);
    const hasCellRefs = innerKeys.some(k => /^[A-Z]+\d+$/.test(k));
    if (!hasCellRefs && innerKeys.length > 0) {
      return firstValue;
    }
  }
  return data;
}

export interface GridData {
  headers: string[];
  rows: (string | number | null)[][];
  rowClasses: (string | undefined)[];
}

export function buildGrid(cells: Record<string, any>): GridData {
  let maxCol = 1;
  let maxRow = 1;
  for (const ref of Object.keys(cells)) {
    const parsed = parseCellRef(ref);
    if (parsed) {
      if (parsed.col > maxCol) maxCol = parsed.col;
      if (parsed.row > maxRow) maxRow = parsed.row;
    }
  }

  const headers: string[] = [];
  for (let c = 1; c <= maxCol; c++) headers.push(colLabel(c));

  const rows: (string | number | null)[][] = [];
  const rowClasses: (string | undefined)[] = [];
  for (let r = 1; r <= maxRow; r++) {
    const row: (string | number | null)[] = [];
    for (let c = 1; c <= maxCol; c++) {
      const ref = `${colLabel(c)}${r}`;
      const val = cells[ref];
      row.push(val ?? null);
    }
    rows.push(row);

    const typeVal = cells[`A${r}`];
    const typeStr = typeof typeVal === 'string' ? typeVal : undefined;
    rowClasses.push(typeStr ? ROW_TYPE_MAP[typeStr] : undefined);
  }

  return { headers, rows, rowClasses };
}
